import { memo } from 'react'
import { AgentV2MessageBubble, type AgentV2MessageBubbleProps } from '@/agent-v2/components/AgentV2MessageBubble'
import { useAgentV2ReferenceOpensInPanel, useOpenAgentV2Reference } from '@/agent-v2/hooks/useOpenAgentV2Reference'
import { selectAgentV2ReferencesByCitationId } from '@/agent-v2/references/store/selectors'
import { useAppSelector } from '@/store/store-hooks'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import { CaseStatusEnum, isDisplayableCaseStatus } from '@/citator/schema/case-status-schema'
import { CitatorOpenedIn, CitatorPanelSource, CitatorSurface, mapCaseStatusToBadgeType } from '@/analytics/enums/citator.enums'
import { RiaCitatorSection } from '@/analytics/enums/ria.enums'
import { trackCitatorBadgeClicked, trackCitatorPanelOpened } from '@/analytics/utils/citator-analytics'

// Citation callbacks belong inside the memo boundary so transcript updates don't recreate them for every message.
export const AgentV2ChatMessage = memo(function AgentV2ChatMessage(
  props: Omit<AgentV2MessageBubbleProps, 'onCitationClick' | 'referencesOpenInPanel' | 'onCaseStatusClick' | 'onTreatmentFlagClick' | 'workLogItems'>
) {
  const { message, conversationId } = props
  const referencesByCitationId = useAppSelector((state) => selectAgentV2ReferencesByCitationId(state, { conversationId }))
  const openReference = useOpenAgentV2Reference()
  const referencesOpenInPanel = useAgentV2ReferenceOpensInPanel()
  const { trackEvent } = useAnalytics()

  // Treatment-tag click in the citation hover card: the tag is the panel CTA, so the
  // badge-clicked event always fires and panel-opened fires only when the viewer opens.
  // An overruling-risk case opens with its case-citations section expanded.
  const handleCaseStatusClick = (citationId: string) => {
    const reference = referencesByCitationId[citationId]
    const caseStatus = reference?.source_type === 'caselaw' ? reference.case_status : null
    const badgeType = isDisplayableCaseStatus(caseStatus) ? mapCaseStatusToBadgeType(caseStatus) : null
    if (badgeType) trackCitatorBadgeClicked({ trackEvent, surface: CitatorSurface.Chat, badgeType })

    const opened = openReference(reference, message.turnId, {
      defaultTab: CaseLawReferenceTab.citator,
      defaultOpenSection: caseStatus === CaseStatusEnum.overruling_risk ? RiaCitatorSection.CaseCitations : undefined,
    })
    // Fires for both doors — the in-page panel and the draft-workspace new tab now
    // both land on Citation Analysis; the event means "citator detail reached".
    if (opened && badgeType)
      trackCitatorPanelOpened({
        trackEvent,
        surface: CitatorSurface.Chat,
        source: CitatorPanelSource.BadgeClick,
        openedIn: opened === 'panel' ? CitatorOpenedIn.ContentViewer : CitatorOpenedIn.StandaloneViewer,
      })
  }

  // Inline-flag click: the flag component already fired badge-clicked (ChatInline) —
  // one owner per event — so this handler owns only the open and the panel-opened
  // attribution for the flag→citator engagement metric.
  const handleTreatmentFlagClick = (citationId: string) => {
    const reference = referencesByCitationId[citationId]
    const caseStatus = reference?.source_type === 'caselaw' ? reference.case_status : null

    const opened = openReference(reference, message.turnId, {
      defaultTab: CaseLawReferenceTab.citator,
      defaultOpenSection: caseStatus === CaseStatusEnum.overruling_risk ? RiaCitatorSection.CaseCitations : undefined,
    })
    if (opened)
      trackCitatorPanelOpened({
        trackEvent,
        surface: CitatorSurface.ChatInline,
        source: CitatorPanelSource.BadgeClick,
        openedIn: opened === 'panel' ? CitatorOpenedIn.ContentViewer : CitatorOpenedIn.StandaloneViewer,
      })
  }

  return (
    <AgentV2MessageBubble
      {...props}
      workLogItems={message.workLogItems ?? kEmptyWorkLogItems}
      onCitationClick={(citationId) => openReference(referencesByCitationId[citationId], message.turnId)}
      referencesOpenInPanel={referencesOpenInPanel}
      onCaseStatusClick={handleCaseStatusClick}
      onTreatmentFlagClick={handleTreatmentFlagClick}
    />
  )
})

const kEmptyWorkLogItems: AgentV2MessageBubbleProps['workLogItems'] = []
